import React, { useState, useMemo } from 'react'
import { connect } from 'react-redux'
import { withRouter } from 'react-router'
import { useHistory } from 'react-router-dom'
import { Button, OverlayTrigger, Tooltip } from 'react-bootstrap'

import { setQrCodeData, setQrCodeModal, setPreviewData, setPreviewModal, fetchUserData } from 'Manage/actions'
import InviteTeammates from 'Manage/components/AddTeamMembersModal/components/InviteTeammates'
import ShareDropdown from 'components/ShareDropdown/ShareDropdown'
import { showUpgradeCardModal, showHtmlAnalysisModal, UPGRADE_QR_PAYLOAD } from '../../actions'
import { onUpgradePlan, isDevSite, isEligibleForAIEdit, openAIEditor } from '../../utils/general'
import { PLANS } from '../../constants/plans'
import { QR_PAYWALL_DATE } from '../QRCodeModal/QRCodeModal'
import CustomModal from 'components/CustomModal/CustomModal'
import { ACTION_UPDATE } from 'components/CreateUpdateSiteModal/interface'
import SuccessIcon from '../SuccessIcon/SuccessIcon'
import celloService from '../../services/cello'
import ReferralSection from '../ReferralSection/ReferralSection'

import styles from './FirstTimeSuccessModal.module.css'

const FirstTimeSuccessModal = ({ domain, userProfile, dispatch, show, onHide, onSiteAction }) => {
  const history = useHistory()
  const [showCopyTooltip, setShowCopyTooltip] = useState(false)
  const isFreeAccount = !userProfile.productId
  const isB2BUser = userProfile?.userCategory === 'B2B'

  const onQRCode = () => {
    if (userProfile.productId || new Date(userProfile.created) < new Date(QR_PAYWALL_DATE)) {
      dispatch(setQrCodeModal(true))
      dispatch(setQrCodeData(domain.subdomain))
    } else {
      dispatch(
        showUpgradeCardModal({
          ...UPGRADE_QR_PAYLOAD,
          onClick: () => onUpgradePlan({ planId: PLANS.TINY.id, userProfile }, dispatch)
        })
      )
    }
  }

  const onClose = () => {
    const hasCompletedTrial = localStorage.getItem('hasCompletedFreeTrial')
    hasCompletedTrial && dispatch(fetchUserData(false))
    onHide()
  }

  const onViewSite = (site) => {
    if (site?.subdomain) window.open(`https://${site.subdomain}?mode=suggestions`, '_blank')
    else {
      dispatch(setPreviewModal(true))
      dispatch(setPreviewData(site))
    }
  }

  const isAIEligible = useMemo(() => isEligibleForAIEdit(userProfile, domain), [userProfile, domain])

  const onEditAIClick = (e) => {
    e.preventDefault()
    if (!domain?.subdomain) return
    openAIEditor(domain.subdomain, !userProfile.productId)
  }

  const handleEditIconClick = (e) => {
    e.preventDefault()
    onClose()
    history.push(`/manage/update?site=${encodeURIComponent(domain?.subdomain)}&edit_link_name=true`)
    onSiteAction(ACTION_UPDATE, domain)
  }

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(`https://${domain?.subdomain?.replace(/\/$/, '')}`)
      setShowCopyTooltip(true)
      setTimeout(() => setShowCopyTooltip(false), 2000)
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea')
      textArea.value = `https://${domain?.subdomain?.replace(/\/$/, '')}`
      document.body.appendChild(textArea)
      textArea.select()
      document.execCommand('copy')
      document.body.removeChild(textArea)
      setShowCopyTooltip(true)
      setTimeout(() => setShowCopyTooltip(false), 2000)
    }
  }

  const handleGenerateReferralLink = async () => {
    if (!userProfile) return
    try {
      await celloService.initAndOpen(userProfile)
    } catch (error) {
      console.error('Failed to generate referral link:', error)
    }
  }
  // Share functionality - using tiiny.xyz format for sharing
  const redirect_url = `${domain?.subdomain?.replace(/\/$/, '')}.tiiny.xyz/?d=${domain?.tld?.replace(/^\./, '') || 'host'}`

  return (
    <CustomModal
      show={show}
      onHide={onClose}
      aria-labelledby="contained-modal-title-vcenter"
      centered
      size="md"
      enforceFocus={false}
      mobileCloseButton={false}
    >
      <div className={styles.modalContent}>
        <div className={styles.container}>
          <div className={styles.modalRow}>
            {/* Main Content */}
            <div className={styles.mainColumn}>
              <div className={styles.successBox}>
                {/* Close button */}
                <button className={styles.closeButton} onClick={onClose}>
                  <img src="/delete.png" alt="quit" width="16" height="16" />
                </button>

                {/* Success Content - Centered */}
                <div className={styles.centeredContent}>
                  <h1 className={styles.successTitle}>Success!</h1>
                  <div className="flex w-100">
                    <div className={styles.successIcon}>
                      <SuccessIcon width={40} height={40} />
                    </div>
                    <div className="text-start">
                      <p className={styles.projectLiveText}>Your Project is now live</p>

                      {/* Domain Link */}
                      {domain?.subdomain ? (
                        <div className={styles.domainContainer}>
                          <a
                            className={styles.domainLink}
                            href={`https://${domain?.subdomain}?mode=suggestions`}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="SuccessBoxHomePage-a-domain"
                          >
                            {domain?.subdomain}
                          </a>
                        </div>
                      ) : (
                        <h5 className={`${styles.domain} mb-4`}>Your Site is live</h5>
                      )}
                    </div>
                  </div>

                  <div className="d-flex flex-column mt-2 mb-3 align-items-center w-100">
                    {/* Main Action Buttons */}
                    <div className={styles.actionButtons}>
                      <button className={styles.actionButton} onClick={handleEditIconClick}>
                        Update
                      </button>
                      <Button variant="primary" className="flex-grow-1" onClick={() => onViewSite(domain)}>
                        View link
                      </Button>
                    </div>
                    {/* Main Action Buttons */}

                    {/* Secondary Action Buttons */}
                    <div className={`mt-3 ${styles.secondaryActionButtons}`}>
                      {isAIEligible ? (
                        <button className={`${styles.actionButton} ${styles.actionButtonSlim}`} onClick={onEditAIClick}>
                          <img
                            className="me-2"
                            height="20"
                            width="20"
                            src="/assets/icons/edit.svg"
                            alt="Edit with AI"
                          />
                          Edit with AI
                        </button>
                      ) : (
                        <button
                          className={`${styles.actionButton} ${styles.actionButtonSlim}`}
                          onClick={handleEditIconClick}
                        >
                          <img className="me-2" height="20" width="20" src="/assets/icons/edit.svg" alt="Customize" />
                          Customize
                        </button>
                      )}
                      <button className={`${styles.actionButton} ${styles.actionButtonSlim}`} onClick={onQRCode}>
                        <img className="me-2" height="20" width="20" src="/assets/icons/qr-code.svg" alt="QR Code" />
                        QR Code
                      </button>
                      {!isFreeAccount && (
                        <ShareDropdown
                          url={redirect_url}
                          trial={isFreeAccount}
                          className={`${styles.actionButton} ${styles.actionButtonSlim}`}
                          onViewLink={() => onViewSite(domain)}
                        >
                          Share
                        </ShareDropdown>
                      )}
                      {isFreeAccount && (
                        <OverlayTrigger
                          placement="bottom"
                          show={showCopyTooltip}
                          overlay={<Tooltip id="copy-tooltip">Link copied!</Tooltip>}
                        >
                          <button
                            className={`${styles.actionButton} ${styles.actionButtonSlim}`}
                            onClick={handleCopyLink}
                          >
                            <img className="me-2" height="20" width="20" src="/assets/icons/copy-link.svg" alt="Copy" />
                            Copy link
                          </button>
                        </OverlayTrigger>
                      )}
                    </div>
                    {/* Secondary Action Buttons */}

                    <div>{(isB2BUser || !isFreeAccount) && <InviteTeammates userProfile={userProfile} />}</div>

                    {!isB2BUser && isFreeAccount && (
                      <ReferralSection userProfile={userProfile} onGenerateLink={handleGenerateReferralLink} />
                    )}

                    <div className={styles.helpSection}>
                      <span>Something broken? - </span>
                      <a
                        className={styles.helpLink}
                        href="#"
                        onClick={(e) => {
                          e.preventDefault()
                          dispatch(showHtmlAnalysisModal(domain))
                        }}
                      >
                        Analyze issues
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </CustomModal>
  )
}

const mapDispatchToProps = (dispatch) => ({
  dispatch: dispatch
})

export default connect(mapDispatchToProps)(withRouter(FirstTimeSuccessModal))
